/**
 * Live board-day for sewing wall boards. See `filters.ts` `defaultDate` for the 09:00 rule.
 *
 * Both desk and kiosk auto-refresh on the wall-poll cadence, so both re-evaluate the board day
 * every tick. A URL day is honoured only when it is a deliberate look-back (not the auto board
 * day). When the reader is following the rule — no `tran_date`, or still on the last auto day —
 * the hook returns the live board day and writes it into the URL so the next tick can roll past
 * 09:00 and midnight without treating yesterday's auto day as a pinned historical choice.
 */

import { useEffect, useState } from 'react';
import { isValid, parseISO } from 'date-fns';

import { REFRESH_INTERVAL_MS, defaultDate } from './filters';

function isIsoDate(value: string): boolean {
  return /^\d{4}-\d{2}-\d{2}$/.test(value) && isValid(parseISO(value));
}

/**
 * @param urlDate - raw `tran_date` from the query string (`null` when absent)
 * @param onAutoDate - write the live board day into the URL while following the rule (`replace`)
 */
export function useBoardDay(
  urlDate: string | null,
  onAutoDate: (date: string) => void,
): string {
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const id = window.setInterval(() => {
      setNow(new Date());
    }, REFRESH_INTERVAL_MS);
    return () => {
      window.clearInterval(id);
    };
  }, []);

  const boardDate = defaultDate(now);
  // State, not a ref: followingAuto is read during render, and react-hooks/refs forbids ref.current
  // there. lastAuto is the previous auto day still written in the URL after a roll.
  const [lastAuto, setLastAuto] = useState(boardDate);

  const raw = urlDate !== null ? urlDate.trim() : '';
  const pinned = raw !== '' && isIsoDate(raw) ? raw : null;

  // Following the rule: nothing in the URL, still on the last auto day, or already on today's board day.
  const followingAuto =
    pinned === null || pinned === lastAuto || pinned === boardDate;

  // Keep lastAuto in sync while following — during render so the next comparison sees the roll
  // without waiting for an effect (and without reading a ref during render).
  if (followingAuto && lastAuto !== boardDate) {
    setLastAuto(boardDate);
  }

  // URL is the external system; only write when following and the URL is behind the live day.
  useEffect(() => {
    if (!followingAuto) {
      return;
    }
    if (pinned !== boardDate) {
      onAutoDate(boardDate);
    }
  }, [followingAuto, boardDate, pinned, onAutoDate]);

  if (followingAuto) {
    return boardDate;
  }

  // followingAuto is false only when pinned is a deliberate historical ISO day.
  return pinned!;
}
